import { Link, useParams } from "react-router-dom";
import { FadeIn } from "@/components/motion/fade-in";
import { Button } from "@/components/ui/button";
import { ALL_DOC_SLUGS, DOC_SECTIONS } from "@/app/docs/sections";
import { DOC_CONTENT } from "@/app/docs/content";

export function DocPage() {
  const params = useParams();
  // splat — react-router gives us the rest of the path after /docs/
  const slug = (params["*"] ?? "").replace(/\/$/, "");
  const page = DOC_CONTENT[slug];

  if (!page) {
    return (
      <div className="py-16 lg:py-24">
        <FadeIn immediate>
          <p className="text-sm text-muted-foreground">404</p>
          <h1 className="mt-2 text-3xl font-bold tracking-tight">
            We couldn't find that page
          </h1>
          <p className="mt-3 max-w-prose text-muted-foreground">
            <code>{`/docs/${slug || ""}`}</code> doesn't exist. Try one of these:
          </p>
          <ul className="mt-6 list-inside list-disc space-y-1 text-sm">
            {ALL_DOC_SLUGS.slice(0, 8).map((s) => (
              <li key={s}>
                <Link to={`/docs/${s}`} className="underline">
                  /docs/{s || "(index)"}
                </Link>
              </li>
            ))}
          </ul>
          <Button asChild className="mt-8">
            <Link to="/docs">Back to docs home</Link>
          </Button>
        </FadeIn>
      </div>
    );
  }

  // Find prev / next in the sidebar order for footer navigation.
  const flat = DOC_SECTIONS.flatMap((s) => s.links);
  const idx = flat.findIndex((l) => l.slug === slug);
  const prev = idx > 0 ? flat[idx - 1] : null;
  const next = idx < flat.length - 1 ? flat[idx + 1] : null;

  return (
    <article className="py-12 lg:py-16">
      <FadeIn immediate>
        <header className="mb-10 border-b border-border/40 pb-8">
          <p className="text-sm font-medium text-muted-foreground">
            {page.section}
          </p>
          <h1 className="mt-2 text-balance text-4xl font-bold tracking-tight sm:text-5xl">
            {page.title}
          </h1>
          {page.description && (
            <p className="mt-4 text-balance text-lg text-muted-foreground">
              {page.description}
            </p>
          )}
        </header>
      </FadeIn>
      {page.visual && (
        <FadeIn immediate delay={0.04} className="not-prose mb-10">
          {page.visual}
        </FadeIn>
      )}
      <FadeIn immediate delay={0.05}>
        <div className="docs-prose prose prose-zinc max-w-none dark:prose-invert prose-headings:scroll-mt-24 prose-h2:mt-12 prose-h2:border-t prose-h2:border-border/40 prose-h2:pt-8 prose-pre:rounded-xl prose-pre:bg-zinc-950 prose-pre:text-zinc-100 prose-pre:border prose-pre:border-zinc-800 prose-img:rounded-lg prose-a:font-medium prose-a:text-foreground prose-a:underline-offset-4 hover:prose-a:underline">
          {page.body}
        </div>
      </FadeIn>
      <FadeIn delay={0.1}>
        <nav className="mt-16 grid grid-cols-2 gap-4 border-t border-border/40 pt-8">
          {prev ? (
            <Link
              to={`/docs/${prev.slug}`}
              className="group rounded-xl border border-border/60 p-4 transition hover:border-border"
            >
              <p className="text-xs text-muted-foreground">← Previous</p>
              <p className="mt-1 font-medium group-hover:text-foreground">
                {prev.title}
              </p>
            </Link>
          ) : (
            <span />
          )}
          {next ? (
            <Link
              to={`/docs/${next.slug}`}
              className="group rounded-xl border border-border/60 p-4 text-right transition hover:border-border"
            >
              <p className="text-xs text-muted-foreground">Next →</p>
              <p className="mt-1 font-medium group-hover:text-foreground">
                {next.title}
              </p>
            </Link>
          ) : (
            <span />
          )}
        </nav>
      </FadeIn>
    </article>
  );
}
